import { useEffect, useState } from "react";
import {
    Alert,
    Autocomplete,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { OrganizationRoleCompany, RoleDialogState } from "src/pages/Settings/Users/OrganizationRolesSection/types";

type OrganizationRolesDialogProps = {
    open: boolean;
    state: RoleDialogState;
    companyOptions: OrganizationRoleCompany[];
    companyInputValue?: string;
    roleOptions: string[];
    isCompaniesLoading?: boolean;
    isRolesLoading?: boolean;
    isSaving: boolean;
    showPlatformAdminWarning: boolean;
    onCompanyInputChange?: (value: string) => void;
    onClose: () => void;
    onSave: (state: RoleDialogState) => void;
};

export const OrganizationRolesDialog = (props: OrganizationRolesDialogProps) => {
    const {
        open,
        state,
        companyOptions,
        companyInputValue,
        roleOptions,
        isCompaniesLoading = false,
        isRolesLoading = false,
        isSaving,
        showPlatformAdminWarning,
        onCompanyInputChange,
        onClose,
        onSave,
    } = props;
    const { t } = useTranslation();
    const [company, setCompany] = useState<OrganizationRoleCompany | null>(state.company);
    const [roles, setRoles] = useState<string[]>(state.roles);

    useEffect(() => {
        if (open) {
            setCompany(state.company);
            setRoles(state.roles);
        }
    }, [open, state]);

    const isAddMode = state.mode === "add";
    const isSaveDisabled = isSaving || isRolesLoading || !company || (isAddMode && roles.length === 0);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isAddMode ?
                    t("users.organizationRolesAddTitle", "Add Companies & Roles")
                :   t("users.organizationRolesEditTitle", "Edit company roles")}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ paddingTop: (theme) => theme.spacing(1) }}>
                    {showPlatformAdminWarning && (
                        <Alert severity="warning" role="alert">
                            {t(
                                "users.organizationRolesPlatformAdminWarning",
                                "This user is a platform administrator. Changing or removing organization roles may affect platform access.",
                            )}
                        </Alert>
                    )}
                    <Autocomplete
                        value={company}
                        options={companyOptions}
                        inputValue={isAddMode ? companyInputValue : undefined}
                        loading={isCompaniesLoading}
                        disabled={!isAddMode || isSaving}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, newValue) => setCompany(newValue)}
                        onInputChange={(_, newInputValue) => {
                            if (isAddMode) {
                                onCompanyInputChange?.(newInputValue);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("users.organizationRolesCompanyLabel", "Company")}
                                placeholder={t("users.organizationRolesCompanyPlaceholder", "Select company")}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isCompaniesLoading ?
                                                <CircularProgress color="inherit" size={18} />
                                            :   null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <Autocomplete
                        multiple
                        value={roles}
                        options={roleOptions}
                        loading={isRolesLoading}
                        disabled={isRolesLoading || isSaving}
                        onChange={(_, newValue) => setRoles(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("users.organizationRolesRolesLabel", "Roles")}
                                placeholder={t("users.organizationRolesPlaceholder", "Select roles")}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isRolesLoading ?
                                                <CircularProgress color="inherit" size={18} />
                                            :   null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ padding: (theme) => theme.spacing(2, 3, 3) }}>
                <Button onClick={onClose} disabled={isSaving}>
                    {t("users.organizationRolesCancel", "Cancel")}
                </Button>
                <Button
                    variant="primary"
                    disabled={isSaveDisabled}
                    onClick={() => company && onSave({ mode: state.mode, company, roles })}
                >
                    {isSaving ?
                        <CircularProgress color="inherit" size={18} />
                    :   t("users.organizationRolesSave", "Save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
